import { CalendarView } from '../features/schedule/types/calendarTypes';
import { NavigationService } from '../services/NavigationService';

/**
 * Calendar localStorage utilities
 */
const CALENDAR_DATE_KEY = 'calendar_date';

export const getCalendarDateFromStorage = (
  workspaceSlug: string
): string | null => {
  try {
    const key = `${CALENDAR_DATE_KEY}_${workspaceSlug}`;
    return localStorage.getItem(key);
  } catch (error) {
    console.warn('Failed to get calendar date from localStorage:', error);
    return null;
  }
};

export const saveCalendarDateToStorage = (
  workspaceSlug: string,
  date: string
): void => {
  try {
    const key = `${CALENDAR_DATE_KEY}_${workspaceSlug}`;
    localStorage.setItem(key, date);
  } catch (error) {
    console.warn('Failed to save calendar date to localStorage:', error);
  }
};

/**
 * Parses URL parameters for calendar navigation
 */
export interface UrlParams {
  view?: string;
  date?: string;
  trainingId?: string;
  workspaceSlug?: string;
}

/**
 * Parsed calendar state from URL parameters
 */
export interface CalendarState {
  calendarCurrentDate: Date;
  isTrainingMode: boolean;
  isDayDetail: boolean;
  selectedDate: Date | null;
  calendarView: CalendarView;
  trainingIdParam?: string;
  workspaceSlug?: string;
}

/**
 * Parses URL parameters and returns calendar state
 */
export const parseUrlParams = (params: UrlParams): CalendarState => {
  const calendarCurrentDate: Date = params.date
    ? new Date(params.date)
    : new Date();

  const isTrainingMode = Boolean(params.trainingId);
  const isDayDetail = !isTrainingMode && params.view === 'day';
  const selectedDate: Date | null =
    isDayDetail && params.date ? new Date(params.date) : null;

  const calendarView: CalendarView =
    params.view === 'week' || params.view === 'month'
      ? (params.view as CalendarView)
      : 'week';

  return {
    calendarCurrentDate,
    isTrainingMode,
    isDayDetail,
    selectedDate,
    calendarView,
    trainingIdParam: params.trainingId,
    workspaceSlug: params.workspaceSlug,
  };
};

/**
 * Creates navigation helper functions
 */
export const createNavigationHelpers = (
  workspaceSlug: string,
  navigationService: NavigationService,
  formatDate: (date: Date, format: string) => string
) => {
  const navigateToCalendarView = (
    view: CalendarView,
    date: Date,
    replace: boolean = false
  ) => {
    const dateStr = formatDate(date, 'yyyy-MM-dd');
    // Save date to localStorage when navigating to calendar
    saveCalendarDateToStorage(workspaceSlug, dateStr);
    navigationService.toCalendar(workspaceSlug, view, dateStr, { replace });
  };

  const navigateToDay = (date: Date, replace: boolean = false) => {
    const dateStr = formatDate(date, 'yyyy-MM-dd');
    // Save date to localStorage when navigating to day view
    saveCalendarDateToStorage(workspaceSlug, dateStr);
    navigationService.toDay(workspaceSlug, dateStr, {
      replace,
    });
  };

  const navigateToTraining = (trainingId: string) => {
    navigationService.toTraining(workspaceSlug, trainingId);
  };

  return {
    navigateToCalendarView,
    navigateToDay,
    navigateToTraining,
  };
};
